// Plan 14-05 (MUI-06) — PushOptInModal.
// Phase 16 Plan 06 (PUSH-05) — registerWebPush() chained inside onEnable.
//
// First-bot-match push permission opt-in modal per DEC-027. Both CTAs
// POST `/api/onboarding/flag {flag:'pushOptInShown'}` to record the one-shot
// (idempotent on the server side via json_patch).
//
// ARCA-147 — this modal is now rendered by LobbyScreen, not MatchScreen. It
// used to fire synchronously on `runegambit:match-completed`, which rendered
// it over the terminal WIN/LOSE/DRAW overlay. The opt-in is deferred to the
// lobby (via an onboarding sessionStorage baton set on HOME navigation out of
// a completed bot match), so the copy below is written to read correctly in
// that context — no false "it's your turn right now" framing on a just-
// finished bot match.
//
// CRITICAL — Pitfall 3 (14-RESEARCH.md) / Pitfall 1 (16-RESEARCH.md):
// Notification.requestPermission() MUST be called from a user-gesture frame
// (the click handler), NEVER from a useEffect / setTimeout / async chain
// detached from a user event. iOS Safari + several other engines silently
// REJECT requestPermission() calls that are not directly synchronous to a
// click. The component therefore awaits requestPermission as the FIRST
// awaited expression inside the click handler so the synchronous portion
// of the call lands inside the click event tick. The subsequent
// registerWebPush() chain (which does pushManager.subscribe()) lives
// AFTER that first await and is fine — subscribe() does NOT have the same
// gesture restriction; only requestPermission() does.
//
// Phase 16 Plan 06 (PUSH-05): onEnable now chains registerWebPush() after a
// granted permission. The same commit that adds the chain ALSO flips the
// Phase-14 deferral pin at PushOptInModal.test.tsx:143 (Pitfall 10 from
// 16-RESEARCH.md — landing the chain without flipping the pin would break
// the existing assertion that the modal does NOT import registerWebPush).

import { type JSX, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { apiFetch } from '../../net/client'
// Phase 16 Plan 06 (PUSH-05) — static import flips the Phase-14 deferral pin
// (Pitfall 10). Using a regular ES import (NOT dynamic import) to match the
// project's existing modal-import style and keep the call chain inside the
// same task as the click event (Pitfall 1 — user-gesture invariant).
import { registerWebPush } from '../../push/register'
import { setPushOptInShown } from '../../stores/onboarding'
import { Dialog } from '../components/Dialog'

export interface PushOptInModalProps {
  readonly open: boolean
  readonly onDismiss: () => void
}

async function postPushOptInFlag(): Promise<void> {
  try {
    await apiFetch('/api/onboarding/flag', 'POST', {
      body: { flag: 'pushOptInShown' },
    })
  } catch {
    // Idempotent server-side; do not block dismissal.
  }
  // Mirror locally so subsequent renders of MatchScreen do not re-mount the
  // modal before the server-cache refetch lands.
  setPushOptInShown()
}

export function PushOptInModal({
  open,
  onDismiss,
}: PushOptInModalProps): JSX.Element | null {
  // ARCA-82 WS2 — useFocusTrap + scrim/panel styling live in <Dialog>/ModalScrim.

  const onEnable = useCallback(async () => {
    // Pitfall 1 / Pitfall 3 — requestPermission MUST be the FIRST awaited
    // expression inside the user-gesture tick. Browsers (iOS Safari
    // especially) silently no-op on detached calls.
    let perm: NotificationPermission = 'default'
    if (
      typeof Notification !== 'undefined' &&
      'requestPermission' in Notification
    ) {
      try {
        perm = await Notification.requestPermission()
      } catch {
        // requestPermission() can throw in some legacy callback-mode
        // implementations or when called from an insecure origin. The flag
        // POST + dismiss should still proceed; chain is gated on 'granted'
        // below so a throw skips the chain naturally.
      }
    }
    // Phase 16 Plan 06 (PUSH-05) — chain registerWebPush AFTER
    // requestPermission resolves 'granted'. Both calls live inside the same
    // useCallback so the user-gesture invariant (Pitfall 1) is preserved.
    // registerWebPush is itself idempotent + re-entrant-safe via the
    // module-level _registrationPromise cache in register.ts (NY-08-07).
    if (perm === 'granted') {
      try {
        // WR-04 fix — caller already invoked Notification.requestPermission()
        // above and verified 'granted'. Pass `skipPermissionPrompt: true` so
        // registerWebPush does NOT call requestPermission a second time. The
        // second call would land past an `await` boundary; on strict Safari
        // user-gesture engines the cached-granted short-circuit is not
        // guaranteed across future versions (Pitfall 1).
        await registerWebPush({ skipPermissionPrompt: true })
      } catch (err) {
        // Best-effort — failure to subscribe does NOT block the opt-in flag
        // POST or modal dismissal. The user can retry from Settings →
        // Notifications. Matches UI-SPEC §"Toast & State Feedback" silent-
        // optimistic behavior (no extra failure modal).
        console.error(
          '[PushOptInModal] registerWebPush failed (non-fatal)',
          err,
        )
      }
    }
    await postPushOptInFlag()
    onDismiss()
  }, [onDismiss])

  const onLater = useCallback(async () => {
    await postPushOptInFlag()
    onDismiss()
  }, [onDismiss])

  if (!open) return null

  return (
    <Dialog
      onClose={onDismiss}
      testId="push-optin-modal"
      labelledBy="push-optin-title"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <h2
          id="push-optin-title"
          style={{
            margin: '0 0 8px',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: '20px',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="match.push.optin.title"
            defaultMessage="Want match updates?"
          />
        </h2>
        <p
          style={{
            margin: '0 0 16px',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: 1.5,
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="match.push.optin.body"
            defaultMessage="We'll send a push when it's your turn in an active match. No spam."
          />
        </p>
        <button
          type="button"
          onClick={onEnable}
          data-cta="push-optin-enable"
          style={{
            width: '100%',
            height: '56px',
            backgroundColor: 'var(--color-cta-primary)',
            color: 'var(--color-inkwell)',
            border: '3px solid var(--color-text-stroke)',
            borderRadius: '12px',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: '20px',
            cursor: 'pointer',
          }}
        >
          <FormattedMessage
            id="match.push.optin.cta"
            defaultMessage="Turn on nudges"
          />
        </button>
        <button
          type="button"
          onClick={onLater}
          data-cta="push-optin-later"
          style={{
            width: '100%',
            height: '44px',
            marginTop: '8px',
            backgroundColor: 'transparent',
            color: 'var(--color-text-primary)',
            border: '2px solid var(--color-surface-stroke)',
            borderRadius: '12px',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <FormattedMessage
            id="match.push.optin.cta_later"
            defaultMessage="Maybe later"
          />
        </button>
      </div>
    </Dialog>
  )
}
